import { useMemo, useState } from 'react';
import './Pricing.css';

const BILLING_CYCLE = {
  MONTHLY: 'monthly',
  ANNUAL: 'annual'
};

const PLANS = [
  {
    id: 'free',
    eyebrow: 'Zopeo free',
    name: 'Free',
    tagline: 'For stores testing the waters. No card required.',
    monthlyPrice: 'free',
    annualTotal: 0,
    annualSavings: 0,
    featured: false,
    featureLabel: '',
    ctaLabel: 'Get started free',
    ctaStyle: 'ghost',
    ctaSub: 'No setup fee',
    usageNote: 'First 30 confirmed bookings/month free, then ₹12 each',
    features: [
      { text: '1 section, 1 counter', available: true },
      { text: '50 queue entries/day', available: true },
      { text: 'QR code generation', available: true },
      { text: 'OTP billing verification', available: true },
      { text: 'WhatsApp notifications', available: true },
      { text: 'No appointments', available: false, dimmed: true },
      { text: 'No offers module', available: false, dimmed: true },
      { text: 'No analytics', available: false, dimmed: true }
    ]
  },
  {
    id: 'pro',
    eyebrow: 'Zopeo pro',
    name: 'Pro',
    tagline: 'Independent stores, salons, clinics, food courts.',
    monthlyPrice: 999,
    annualTotal: 9990,
    annualSavings: 1998,
    featured: true,
    featureLabel: 'Everything in Free, plus',
    ctaLabel: 'Start 14-day trial',
    ctaStyle: 'fill',
    ctaSub: 'No card during trial',
    usageNote: '+ ₹12 per OTP-verified confirmed booking',
    features: [
      { text: 'Unlimited sections and counters', available: true },
      { text: 'Unlimited queue entries', available: true },
      { text: 'Appointments - all services + deposit collection', available: true },
      { text: 'Offers, deals and memberships', available: true },
      { text: 'Analytics - 1 year history + CSV export', available: true },
      { text: 'Multi-branch dashboard', available: true },
      { text: 'WhatsApp notifications', available: true },
      { text: '3 staff logins', available: true }
    ]
  },
  {
    id: 'premium',
    eyebrow: 'Zopeo premium',
    name: 'Premium',
    tagline: 'Growing chains and multi-location operators.',
    monthlyPrice: 2499,
    annualTotal: 24990,
    annualSavings: 4998,
    featured: false,
    featureLabel: 'Everything in Pro, plus',
    ctaLabel: 'Start 14-day trial',
    ctaStyle: 'outline',
    ctaSub: 'No card during trial',
    usageNote: '+ ₹12 per OTP-verified confirmed booking',
    features: [
      { text: 'Enterprise hierarchy - multi-store dashboard', available: true },
      { text: 'Rolled-up analytics across locations', available: true },
      { text: 'Mall heatmap access (view + contribute)', available: true },
      { text: 'Skip-queue pass management', available: true },
      { text: 'API access - full read/write', available: true },
      { text: 'Custom WhatsApp sender ID', available: true },
      { text: 'Unlimited staff logins', available: true },
      { text: 'Priority support + account manager', available: true }
    ]
  }
];

const COMPARE_GROUPS = [
  {
    title: 'Billing',
    rows: [
      { feature: 'Monthly subscription', free: '₹0', pro: '₹999', premium: '₹2,499' },
      { feature: 'Per confirmed booking', free: '₹12 (30 free/mo)', pro: '₹12', premium: '₹12' },
      { feature: 'Percentage commission', free: 'None', pro: 'None', premium: 'None' },
      { feature: 'Instant invoice per payment', free: true, pro: true, premium: true }
    ]
  },
  {
    title: 'Queue management',
    rows: [
      { feature: 'Sections', free: '1', pro: 'Unlimited', premium: 'Unlimited' },
      { feature: 'Counters / zones', free: '1', pro: 'Unlimited', premium: 'Unlimited' },
      { feature: 'Daily queue entries', free: '50', pro: 'Unlimited', premium: 'Unlimited' },
      { feature: 'OTP billing verification', free: true, pro: true, premium: true },
      { feature: 'WhatsApp notifications', free: true, pro: true, premium: true },
      { feature: 'Skip-queue passes', free: false, pro: false, premium: true }
    ]
  },
  {
    title: 'Appointments',
    rows: [
      { feature: 'Booking system', free: false, pro: true, premium: true },
      { feature: 'Recurring slot schedules', free: false, pro: true, premium: true },
      { feature: 'Deposit collection', free: false, pro: true, premium: true },
      { feature: 'Automated reminders', free: false, pro: true, premium: true }
    ]
  },
  {
    title: 'Offers, deals and memberships',
    rows: [
      { feature: 'Offers and deals module', free: false, pro: true, premium: true },
      { feature: 'Memberships', free: false, pro: true, premium: true },
      { feature: 'Queue feed upsell offers', free: false, pro: true, premium: true },
      { feature: 'Offer analytics', free: false, pro: true, premium: true }
    ]
  },
  {
    title: 'Analytics and data',
    rows: [
      { feature: 'Analytics dashboard', free: false, pro: '1 year', premium: 'Full' },
      { feature: 'CSV export', free: false, pro: true, premium: true },
      { feature: 'Mall heatmap access', free: false, pro: false, premium: true },
      { feature: 'API access', free: false, pro: false, premium: true }
    ]
  },
  {
    title: 'Team and enterprise',
    rows: [
      { feature: 'Staff logins', free: '1', pro: '3', premium: 'Unlimited' },
      { feature: 'Enterprise hierarchy', free: false, pro: false, premium: true },
      { feature: 'Custom WhatsApp sender', free: false, pro: false, premium: true },
      { feature: 'Priority support + manager', free: false, pro: false, premium: true }
    ]
  }
];

const ADDONS = [
  {
    id: 'flash',
    iconClass: 'tone-accent',
    name: 'Flash deal boost',
    description: 'Push your flash deal to Zopeo users within 5km who are not yet in the mall. Shown in the home spotlight feed.',
    price: '₹299 per campaign',
    sub: 'Per campaign · expires with your deal'
  },
  {
    id: 'featured',
    iconClass: 'tone-cyan',
    name: 'Featured spotlight placement',
    description: 'Your store appears at the top of the in-store spotlight section for shoppers on that floor for one full week.',
    price: '₹499 per week',
    sub: 'Per week · floor-targeted'
  },
  {
    id: 'report',
    iconClass: 'tone-soft',
    name: 'Offer performance report',
    description: 'Detailed offer funnel: impressions, taps, claims, redemptions, and estimated revenue influenced via OTP.',
    price: 'Included on Premium',
    sub: 'Automatically generated per offer'
  }
];

const MALL_ITEMS = [
  'Live footfall heatmap across all floors',
  'Tenant-level queue and conversion analytics',
  'Mall entry gate integration',
  'FMCG and brand data insights',
  'Smart city compliance reporting',
  'White-labelled customer app option'
];

const FAQS = [
  {
    question: 'Can I try before paying?',
    answer: 'Yes - every paid plan comes with a ',
    highlight: '14-day free trial',
    tail: ', no card required. Free plan is free forever with limits.'
  },
  {
    question: 'How does the ₹12 per-booking fee work?',
    answer: 'You pay ₹12 only when a booking is confirmed and OTP-verified at your counter. Walk-ins are free, the Free plan includes 30 confirmed bookings a month before the fee applies, and it is billed monthly in arrears — ',
    highlight: 'never a percentage of your sale',
    tail: '.'
  },
  {
    question: 'What happens if I exceed my daily queue limit on Free?',
    answer: 'Queues stop accepting new entries for the day. Existing entries continue normally. You get an upgrade prompt and no data is lost.'
  },
  {
    question: 'Do customers pay to use Zopeo?',
    answer: 'No. The customer-facing app is ',
    highlight: 'completely free',
    tail: ' for your customers.'
  },
  {
    question: 'Can I switch plans mid-month?',
    answer: 'Yes. Upgrading is immediate and prorated. Downgrading starts from the next billing cycle.'
  },
  {
    question: 'Is there a setup fee?',
    answer: 'No. Zopeo has no setup fee on any plan — you only pay the monthly subscription plus ₹12 per confirmed booking.'
  },
  {
    question: 'Can I manage multiple stores from one account?',
    answer: 'Multi-store management with rolled-up analytics across locations is available on the ',
    highlight: 'Premium plan',
    tail: '.'
  }
];

const formatINR = (value) => Number(value || 0).toLocaleString('en-IN');

const PriceDisplay = ({ monthly, annualTotal, billingCycle, annualSavings }) => {
  if (monthly === 'free') {
    return (
      <div className="price-display">
        <div className="price-value is-free">Free</div>
        <div className="price-sub">Forever. No credit card.</div>
      </div>
    );
  }

  if (billingCycle === BILLING_CYCLE.ANNUAL) {
    return (
      <div className="price-display">
        <div className="price-value annual">₹{formatINR(annualTotal)}/yr</div>
        <div className="price-savings">₹{formatINR(annualTotal)}/yr - save ₹{formatINR(annualSavings)}</div>
      </div>
    );
  }

  return (
    <div className="price-display">
      <div className="price-value">₹{formatINR(monthly)}/mo</div>
      <div className="price-sub">Billed monthly</div>
    </div>
  );
};

const PlanFeatureDot = ({ available }) => (
  <span className={`plan-feature-dot ${available ? 'available' : 'unavailable'}`} aria-hidden="true">
    <span className="inner" />
  </span>
);

const CompareValue = ({ value }) => {
  if (typeof value === 'boolean') {
    return (
      <span className={`compare-status ${value ? 'yes' : 'no'}`} aria-hidden="true">
        <span className="inner" />
      </span>
    );
  }
  return <span className="compare-partial">{value}</span>;
};

const Pricing = ({
  onGetStartedFree = () => {},
  onStartTrial = () => {},
  onContactSales = () => {},
  onClaimBetaOffer = () => {}
}) => {
  const [billingCycle, setBillingCycle] = useState(BILLING_CYCLE.MONTHLY);
  const plans = useMemo(() => PLANS, []);

  return (
    <div className="pricing-page">
      <section className="pricing-hero">
        <p className="pricing-eyebrow">Simple, transparent pricing</p>
        <h1 className="pricing-title">The right plan for <span>every</span> store</h1>
        <p className="pricing-subtitle">
          From your first QR code to managing 40 stores across a mall chain - Zopeo grows with you.
          Every plan is a flat monthly subscription plus ₹12 per OTP-verified confirmed booking - never a percentage of your revenue.
        </p>

        <div className="pricing-billing-toggle" role="tablist" aria-label="Billing cycle">
          <button
            type="button"
            className={`billing-tab ${billingCycle === BILLING_CYCLE.MONTHLY ? 'active' : ''}`}
            onClick={() => setBillingCycle(BILLING_CYCLE.MONTHLY)}
          >
            Monthly
          </button>
          <button
            type="button"
            className={`billing-tab ${billingCycle === BILLING_CYCLE.ANNUAL ? 'active' : ''}`}
            onClick={() => setBillingCycle(BILLING_CYCLE.ANNUAL)}
          >
            Annual
            <span className="annual-badge">2 months free</span>
          </button>
        </div>
      </section>

      <section className="plans-wrap">
        <div className="plans-grid">
          {plans.map((plan) => (
            <article key={plan.id} className={`plan-card ${plan.featured ? 'featured' : ''}`}>
              {plan.featured ? <div className="plan-popular">Most popular</div> : null}

              <div className="plan-header">
                <p className={`plan-eyebrow ${plan.featured ? 'featured' : ''}`}>{plan.eyebrow}</p>
                <h2 className="plan-name">{plan.name}</h2>
                <p className="plan-tagline">{plan.tagline}</p>
                <PriceDisplay
                  monthly={plan.monthlyPrice}
                  annualTotal={plan.annualTotal}
                  annualSavings={plan.annualSavings}
                  billingCycle={billingCycle}
                />
                {plan.usageNote ? <p className="plan-usage-note">{plan.usageNote}</p> : null}
              </div>

              <div className="plan-body">
                {plan.featureLabel ? <p className="plan-feature-label">{plan.featureLabel}</p> : null}
                <ul className="plan-features">
                  {plan.features.map((feature) => (
                    <li key={`${plan.id}-${feature.text}`} className={`plan-feature-item ${feature.dimmed ? 'dimmed' : ''}`}>
                      <PlanFeatureDot available={feature.available} />
                      <span>{feature.text}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="plan-footer">
                <button
                  type="button"
                  className={`plan-cta ${plan.ctaStyle}`}
                  onClick={() => {
                    if (plan.id === 'free') {
                      onGetStartedFree();
                      return;
                    }
                    onStartTrial(plan.id);
                  }}
                >
                  {plan.ctaLabel}
                </button>
                <p className="plan-cta-sub">{plan.ctaSub}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="beta-banner-wrap">
        <div className="beta-banner">
          <span className="beta-dot" aria-hidden="true" />
          <p className="beta-copy">
            <strong>Beta launch offer:</strong> First 10 merchants who upgrade to Premium get it locked at <strong>₹1,499/month</strong> for life. No setup fee, ever.
          </p>
          <button type="button" className="beta-link" onClick={onClaimBetaOffer}>
            Claim offer ->
          </button>
        </div>
      </section>

      <section className="compare-section">
        <h3 className="section-title">Compare all features</h3>
        <p className="section-subtitle">See exactly what you get on each plan</p>

        <div className="compare-table" role="table" aria-label="Feature comparison">
          <div className="compare-row compare-head" role="row">
            <div role="columnheader">Feature</div>
            <div role="columnheader">Free</div>
            <div role="columnheader" className="growth-col">Pro</div>
            <div role="columnheader">Premium</div>
          </div>

          {COMPARE_GROUPS.map((group) => (
            <div key={group.title} className="compare-group">
              <div className="compare-group-title">{group.title}</div>
              {group.rows.map((row) => (
                <div className="compare-row compare-body-row" role="row" key={`${group.title}-${row.feature}`}>
                  <div className="compare-feature">{row.feature}</div>
                  <div><CompareValue value={row.free} /></div>
                  <div className="growth-col"><CompareValue value={row.pro} /></div>
                  <div><CompareValue value={row.premium} /></div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </section>

      <section className="addons-section">
        <p className="pricing-eyebrow centered">Offer campaign add-ons</p>
        <p className="addons-subtitle">
          Available on Pro and Premium plans. Pay only when you run a campaign.
        </p>

        <div className="addons-grid">
          {ADDONS.map((addon) => (
            <article className="addon-card" key={addon.id}>
              <span className={`addon-icon ${addon.iconClass}`} aria-hidden="true" />
              <h4>{addon.name}</h4>
              <p>{addon.description}</p>
              <div className="addon-price">{addon.price}</div>
              <div className="addon-sub">{addon.sub}</div>
            </article>
          ))}
        </div>
      </section>

      <section className="mall-section">
        <div className="mall-left">
          <p className="mall-eyebrow">For mall operators and large venues</p>
          <h3 className="mall-title">Zopeo for malls</h3>
          <p className="mall-description">
            Running a mall with 20+ tenants? Get the same Zopeo platform with expanded tenant-level insights, heatmaps, and one control surface across your full property.
          </p>
          <ul className="mall-list">
            {MALL_ITEMS.map((item) => (
              <li key={item}>
                <span className="mall-bullet" aria-hidden="true" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="mall-right">
          <button type="button" className="mall-cta" onClick={onContactSales}>
            Contact sales
          </button>
          <p>Custom pricing · Implementation included</p>
        </div>
      </section>

      <section className="faq-section">
        <p className="pricing-eyebrow centered">Common questions</p>
        <div className="faq-grid">
          {FAQS.map((faq) => (
            <article className="faq-card" key={faq.question}>
              <h4>{faq.question}</h4>
              <p>
                {faq.answer}
                {faq.highlight ? <strong>{faq.highlight}</strong> : null}
                {faq.tail || ''}
              </p>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Pricing;
